import React, { useEffect } from "react";
import "../../../styles/products-styling/cleaned/product.css";
import CTABanner from "../../generalComponents/banners/CTABanner";
import { sendSlackMessageTracked } from "../../../models/notifications";
import { LoyaltyProgramImage } from "../../../assets/assets";

/* ─── User Growth Bar Chart ──────────────────────────────────────────────── */
const UserGrowthChart = () => {
    const bars = [
        { label: "Wk 1",   value: 60,   display: "60K" },
        { label: "Wk 2",   value: 200,  display: "200K" },
        { label: "Wk 3",   value: 420,  display: "420K" },
        { label: "Wk 4",   value: 650,  display: "650K" },
        { label: "Wk 5",   value: 800,  display: "800K" },
        { label: "Record", value: 1200, display: "1.2M", highlight: true },
    ];
    const MAX = 1200;
    const H   = 150;

    return (
        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
            <div className="flex items-baseline justify-between mb-6">
                <p className="text-sm font-semibold text-gray-800">Concurrent live users</p>
                <span className="text-xs text-gray-400">weeks after launch</span>
            </div>
            <div className="flex items-end gap-2" style={{ height: H + 40 }}>
                {bars.map((b) => {
                    const barH = Math.round((b.value / MAX) * H);
                    return (
                        <div key={b.label} className="flex-1 flex flex-col items-center gap-1">
                            <span className={`text-xs font-bold ${b.highlight ? "text-indigo-600" : "text-gray-400"}`}>
                                {b.display}
                            </span>
                            <div
                                className={`w-full rounded-t-xl ${b.highlight ? "bg-indigo-500" : "bg-indigo-100"}`}
                                style={{ height: barH }}
                            />
                            <span className="text-xs text-gray-400 mt-1 text-center leading-tight">{b.label}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

/* ─── Hook Model Cycle Diagram ───────────────────────────────────────────── */
const HookModelDiagram = () => {
    const phases = [
        { num: "01", label: "Trigger",         detail: "Push notification + internal desire to play",     accent: "#6366f1", bg: "#eef2ff" },
        { num: "02", label: "Action",           detail: "Find a friend · Charge up · Connect to Wi-Fi",   accent: "#3b82f6", bg: "#eff6ff" },
        { num: "03", label: "Variable Reward",  detail: "The prize money — unpredictable, addictive",      accent: "#10b981", bg: "#f0fdf4" },
        { num: "04", label: "Investment",       detail: "Answer questions, interact, get hooked again",    accent: "#8b5cf6", bg: "#f5f3ff" },
    ];

    return (
        <div>
            <div className="grid grid-cols-2 gap-3">
                {phases.map((p) => (
                    <div key={p.num} className="rounded-2xl p-5 border border-white" style={{ backgroundColor: p.bg }}>
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs font-bold opacity-40" style={{ color: p.accent }}>{p.num}</span>
                            <span className="text-sm font-bold" style={{ color: p.accent }}>{p.label}</span>
                        </div>
                        <p className="text-xs text-gray-500 leading-relaxed">{p.detail}</p>
                    </div>
                ))}
            </div>
            <div className="mt-4 flex items-center gap-2 px-1">
                {phases.map((p, i) => (
                    <React.Fragment key={p.num}>
                        <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: p.accent }} />
                        {i < phases.length - 1 && (
                            <div className="flex-1 h-px bg-gray-200" />
                        )}
                    </React.Fragment>
                ))}
                <span className="text-xs text-gray-400 ml-2 shrink-0">↩ loops back</span>
            </div>
        </div>
    );
};

/* ─── Incentive Breakdown ────────────────────────────────────────────────── */
const IncentiveBreakdown = () => {
    const rows = [
        { label: "Cash Prize",     impact: 95, color: "#6366f1" },
        { label: "Community",      impact: 72, color: "#3b82f6" },
        { label: "Referral Codes", impact: 68, color: "#10b981" },
        { label: "Live Feed",      impact: 81, color: "#f59e0b" },
    ];

    return (
        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
            <p className="text-sm font-semibold text-gray-800 mb-5">User acquisition drivers</p>
            <div className="space-y-4">
                {rows.map((r) => (
                    <div key={r.label}>
                        <div className="flex justify-between text-xs mb-1.5">
                            <span className="text-gray-600 font-medium">{r.label}</span>
                            <span className="font-bold" style={{ color: r.color }}>{r.impact}%</span>
                        </div>
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                                className="h-full rounded-full"
                                style={{ width: `${r.impact}%`, backgroundColor: r.color }}
                            />
                        </div>
                    </div>
                ))}
            </div>
            <p className="text-xs text-gray-400 mt-4">Relative contribution to user growth — illustrative</p>
        </div>
    );
};


/* ─── Main Component ─────────────────────────────────────────────────────── */
const UsersUsersUsersComponent = () => {

    useEffect(() => {
        sendSlackMessageTracked("Lead is visiting the Users Users Users case study page.", "#website-updates");
    }, []);

    return (
        <div className="bg-white">

            {/* ── HERO ──────────────────────────────────────────────────────── */}
            <div className="bg-gray-950 text-white">
                <div className="max-w-5xl mx-auto px-6 pt-20 pb-16">
                    <p className="text-xs font-semibold tracking-widest text-indigo-400 uppercase mb-5">Case Study</p>
                    <h1 className="text-6xl lg:text-7xl font-black leading-none mb-6 tracking-tight">
                        Users<br />Users<br />Users
                    </h1>
                    <p className="text-xl text-gray-400 max-w-xl leading-relaxed mb-10">
                        Companies, businesses, and brands all have one thing in common…customers. Whether they are called users,
                        consumers, or even fans, these organizations all aim to facilitate a relationship with these different
                        classifications. It drives their fundamental existence and profits. Capturing a large number of individuals
                        from these categories is the ultimate goal for them, many have tried and failed, but a few have succeeded.
                        This article will analyze HQ Trivia and its success in capturing users.
                    </p>

                    {/* Hero stats */}
                    <div className="flex flex-wrap gap-8">
                        {[
                            { value: "800K+", label: "Concurrent users — weeks after launch" },
                            { value: "1.2M",  label: "Record live players" },
                            { value: "2×/day", label: "Live trivia sessions" },
                        ].map((s) => (
                            <div key={s.label}>
                                <p className="text-4xl font-black text-white">{s.value}</p>
                                <p className="text-sm text-gray-500 mt-1 max-w-[160px] leading-snug">{s.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── ARTICLE BODY ──────────────────────────────────────────────── */}
            <div className="max-w-4xl mx-auto px-6 py-16 space-y-20">

                {/* ── THE APP THAT GOT 1.2M PEOPLE LIVE ── */}
                <section>
                    <p className="text-xs font-semibold tracking-widest text-indigo-500 uppercase mb-3">The Company</p>
                    <h2 className="text-3xl font-bold text-gray-900 mb-8 leading-snug">
                        The App That Got 1.2 Million People Live at the Same Time
                    </h2>

                    {/* Two-column: text + image */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center mb-10">
                        <div className="space-y-5 text-gray-700 text-lg leading-relaxed">
                            <p>
                                HQ Trivia was a mobile trivia game that pitted you against people worldwide but in a live setting.
                                You'd log in twice a day and a host would lead contests where questions would progressively get more
                                challenging. Awards included virtual prizes, but most notably cash prizes, that drove people to tune
                                in to have a chance at winning a cash prize. Driving the numbers of concurrent live users to over
                                800,000 only weeks after their launch, and hitting a record 1.2 million live players on their app
                                shortly after. They gained a massive reputation and accumulated tons of users seemingly overnight…but
                                what was their secret?
                            </p>
                        </div>
                        <div className="rounded-3xl overflow-hidden shadow-lg border border-gray-100">
                            <img
                                src={LoyaltyProgramImage}
                                alt="HQ Trivia user community"
                                className="w-full h-64 object-cover"
                            />
                        </div>
                    </div>

                    {/* Growth chart */}
                    <UserGrowthChart />
                </section>

                {/* ── WHY EVERYONE COULDN'T STOP PLAYING ── */}
                <section id="incentive">
                    <p className="text-xs font-semibold tracking-widest text-indigo-500 uppercase mb-3">The Incentive</p>
                    <h2 className="text-3xl font-bold text-gray-900 mb-8 leading-snug">
                        Why Everyone Couldn't Stop Playing
                    </h2>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                        <div className="space-y-5 text-gray-700 text-lg leading-relaxed">
                            <p>
                                HQ grew users fast and in a short amount of time, and this is mainly attributed to their model. The
                                biggest thing for a user was winning the cash prize that was offered from the trivia. This was the
                                main thing that initially attracted the users to the platform, human nature shows that winning is
                                favorable and when pairing that with a game like trivia it becomes fun for the user, and they keep
                                coming back. This was the main factor that drove so many users to the game, the fact that you can
                                tune in twice a day to win money sounded appealing. Making that the first incentive built a community
                                around their brand, which was further exemplified through social media. Where they would shout out
                                specific users that were early to their platform, this had a significant impact on community building
                                that connected more users to join.
                            </p>
                            <p>
                                Referrals were a unique feature implemented that allowed users to earn an extra 'life' or 'chance' at
                                the contest by sharing a referral code with friends. This drove thousands of downloads and even more
                                impressions as it was an organic way of growing. A celebrity that shared their code with an extensive
                                fan base for extra lives on social media created even more awareness and downloads for the app.
                            </p>
                            <p>
                                The live feed was another crucial cog in the machine that brought users. By having the contest in a
                                real-time setting, it created unity among players and a sense of involvement. Making users feel a part
                                of a collective group, they see the appeal in a contested environment where they could win against
                                others. Pairing that with the real-time live setting, it emulates the impact and scope of sports
                                matches in stadiums. But instead of a physical stadium where fans aren't playing against each other,
                                HQ Trivia, does get thousands of 'fans' or users to play and compete.
                            </p>
                        </div>

                        {/* Incentive chart */}
                        <div className="lg:sticky lg:top-8">
                            <IncentiveBreakdown />
                        </div>
                    </div>
                </section>

                {/* ── THE SCIENCE BEHIND WHY YOU CAME BACK ── */}
                <section id="hook">
                    <p className="text-xs font-semibold tracking-widest text-indigo-500 uppercase mb-3">The Hook</p>
                    <h2 className="text-3xl font-bold text-gray-900 mb-8 leading-snug">
                        The Science Behind Why You Came Back Every Day
                    </h2>

                    <div className="space-y-5 text-gray-700 text-lg leading-relaxed mb-10">
                        <p>
                            In addition to combining community building, referral codes, and the live feed, HQ used the Hook
                            model (HM). Consisting of a 4 phase process: Trigger, Action, Variable Reward, and Investment, it
                            serves as a model used by businesses to create products and services particularly geared for habitual
                            use by consumers.
                        </p>
                        <p>
                            HQ deployed the HM on the platform first through the initial trigger or phase one, What gets the
                            user to the platform? (the external) and, What does the user want? (the internal) The external
                            trigger came in the form of a push notification broadcasting a live trivia set to start soon, and
                            the internal trigger was the user participating. Then comes the action in phase two, in which HQ
                            prompts the user with actions such as "Finding a smart friend, Turning up the volume, Charging up,
                            and Connecting to Wi-Fi" All in an effort to get the user ready and hyped to start the contest.
                            Phase three variable reward is as expected the prize money, and finally, phase four is the
                            investment. In which the user answers the trivia questions and interacts with the platform, just to
                            be hooked and roped back in the process with the trigger again.
                        </p>
                    </div>

                    <HookModelDiagram />
                </section>

                {/* ── THE LESSON EVERY BUSINESS NEEDS TO HEAR ── */}
                <section id="ending">
                    <p className="text-xs font-semibold tracking-widest text-indigo-500 uppercase mb-3">Ending</p>
                    <h2 className="text-3xl font-bold text-gray-900 mb-8 leading-snug">
                        The Lesson Every Business Needs to Hear
                    </h2>

                    <div className="space-y-5 text-gray-700 text-lg leading-relaxed mb-10">
                        <p>
                            Although HQ's dominance wouldn't last long, an important lesson can be learned from how they
                            accumulated users. By using various methods and tapping into human nature and the desire to win,
                            they were able to amass a significant user base that was also reoccurring. Employing the Hook Model
                            in a gamified way such as trivia showcased the power it has on the success of a business and its
                            goal of getting consumers.
                        </p>
                    </div>

                    <div className="bg-gray-950 rounded-3xl px-10 py-12">
                        <p className="text-3xl font-bold text-white leading-snug">
                            Imagine a thing that could facilitate such interactions…for every business.
                        </p>
                    </div>
                </section>

                {/* ── SOURCES ── */}
                <section>
                    <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-5">Sources</p>
                    <div className="space-y-3">
                        {[
                            { title: "The Magic of HQ Trivia",                                                  host: "blog.producthunt.com" },
                            { title: "The Spectacular Rise and Fall of HQ Trivia",                              host: "bettermarketing.pub" },
                            { title: "Millions of People Played HQ Trivia. What Happened to It?",               host: "theringer.com" },
                            { title: "What Happened To HQ Trivia? Here's Why The Popular Quiz App Failed",      host: "productmint.com" },
                            { title: "Hook Model",                                                               host: "productplan.com" },
                        ].map((s) => (
                            <div key={s.title} className="flex items-baseline gap-3">
                                <span className="text-gray-300 text-sm shrink-0">—</span>
                                <span className="text-sm text-gray-700">{s.title}</span>
                                <span className="text-xs text-gray-400 shrink-0">{s.host}</span>
                            </div>
                        ))}
                    </div>
                </section>

                {/* ── CTA ── */}
                <div className="flex justify-center pt-4">
                    <CTABanner
                        text="Ready to build your own user base?"
                        ctaURL="/pricing"
                        ctaString="Get started"
                    />
                </div>

            </div>
        </div>
    );
};

export default UsersUsersUsersComponent;
